import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Users, GraduationCap, TrendingUp, Eye, RefreshCw, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const AdminPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use relative URL for Vercel (same domain)
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const registrationsUrl = apiBase ? `${apiBase}/api/registrations` : '/api/registrations';
      const statsUrl = apiBase ? `${apiBase}/api/registrations/stats/summary` : '/api/registrations/stats/summary';
      
      console.log('Fetching registrations from:', registrationsUrl);

      // Fetch registrations
      const regResponse = await fetch(registrationsUrl);
      console.log('Reg Response Status:', regResponse.status);

      if (!regResponse.ok) {
        const errorText = await regResponse.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch: ${regResponse.status} ${regResponse.statusText}. ${errorText}`);
      }

      const regData = await regResponse.json();
      console.log('Reg Data:', regData);
      
      if (regData.success !== false) {
        // Handle both success: true and success not being false
        setRegistrations(regData.data || regData || []);
      } else {
        console.warn('API returned success: false', regData);
        setRegistrations([]);
      }

      // Fetch stats
      try {
        const statsResponse = await fetch(statsUrl);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          if (statsData.success !== false) {
            setStats(statsData.data || statsData);
          }
        }
      } catch (statsError) {
        console.warn('Failed to fetch stats:', statsError);
        // Don't set error for stats failure, it's not critical
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Gagal memuat data. Pastikan API endpoint tersedia dan database terhubung.');
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrationDetail = async (id) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/registrations/${id}`);
      const data = await response.json();

      if (data.success) {
        setSelectedRegistration(data.data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error('Error fetching detail:', error);
    }
  };

  const handleExportCSV = async () => {
    // Client-side CSV export as fallback
    if (registrations.length === 0) {
      alert('Tidak ada data untuk di-export');
      return;
    }

    try {
      // Create CSV content
      const headers = ['Nama Lengkap', 'Program', 'Kelas', 'Telepon', 'Tanggal Daftar'];
      const csvContent = [
        headers.join(','),
        ...registrations.map(reg => [
          `"${reg.nama_lengkap}"`,
          reg.program,
          reg.kelas.toUpperCase(),
          reg.telepon,
          new Date(reg.created_at).toLocaleDateString('id-ID')
        ].join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Data_Pendaftaran_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Gagal export data');
    }
  };

  const handleExportExcel = async () => {
    if (registrations.length === 0) {
      alert('Tidak ada data untuk di-export');
      return;
    }

    setIsExporting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/registrations/export/excel`);

      if (!response.ok) {
        throw new Error('Gagal mengunduh file Excel');
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Data_Pendaftaran_BIN_Bimbel_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Gagal export ke Excel. Silakan coba lagi.');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const programBadgeColor = (program) => {
    switch (program) {
      case 'reguler': return 'bg-blue-100 text-blue-700';
      case 'intensif': return 'bg-red-100 text-red-700';
      case 'privat': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const kelasBadgeColor = (kelas) => {
    if (kelas.startsWith('sd')) return 'bg-yellow-100 text-yellow-700';
    if (kelas.startsWith('smp')) return 'bg-purple-100 text-purple-700';
    if (kelas.startsWith('sma')) return 'bg-pink-100 text-pink-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-[#f2f5fa]">
      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#5A9C9B]/10 via-[#f2f5fa] to-[#f26d0f]/10">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
                Admin Dashboard
              </h1>
              <p className="text-lg text-gray-600">
                Data Pendaftaran Siswa BIN Bimbel
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={handleExportCSV}
                disabled={isExporting || registrations.length === 0}
                variant="outline"
                className="border-[#5A9C9B] text-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={handleExportExcel}
                disabled={isExporting || registrations.length === 0}
                className="bg-[#f26d0f] hover:bg-[#e88d2b] text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </Button>
              <Button
                onClick={fetchData}
                className="bg-[#5A9C9B] hover:bg-[#4a8584] text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      {stats && (
        <section className="py-8 px-4 -mt-8">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-2 border-[#5A9C9B]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Pendaftaran</p>
                      <p className="text-3xl font-bold text-[#5A9C9B]">
                        {stats.total_registrations}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#5A9C9B] rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#f26d0f]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Program Aktif</p>
                      <p className="text-3xl font-bold text-[#f26d0f]">
                        {Object.keys(stats.by_program || {}).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#f26d0f] rounded-full flex items-center justify-center">
                      <GraduationCap className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#5A9C9B]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Jenjang</p>
                      <p className="text-3xl font-bold text-[#5A9C9B]">
                        {Object.keys(stats.by_level || {}).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#5A9C9B] rounded-full flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Registrations Table */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Daftar Pendaftaran
              </h2>

              {loading ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Loading...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="bg-red-50 text-red-600 p-4 rounded-lg inline-block">
                    <p className="font-bold">Error loading data</p>
                    <p className="text-sm">{error}</p>
                    <Button onClick={fetchData} variant="outline" className="mt-4 border-red-200 hover:bg-red-100">Try Again</Button>
                  </div>
                </div>
              ) : registrations.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">Belum ada data pendaftaran</p>
                  <p className="text-sm text-gray-500">Data pendaftaran akan muncul di sini setelah ada yang mengisi form pendaftaran.</p>
                  <Button 
                    onClick={fetchData} 
                    variant="outline" 
                    className="mt-4 border-[#5A9C9B] text-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Nama</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Program</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Kelas</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Telepon</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Tanggal</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((reg) => (
                        <tr key={reg.registration_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {reg.nama_lengkap}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={programBadgeColor(reg.program)}>
                              {reg.program}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={kelasBadgeColor(reg.kelas)}>
                              {reg.kelas.toUpperCase()}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-700">{reg.telepon}</td>
                          <td className="py-3 px-4 text-gray-600 text-sm">
                            {new Date(reg.created_at).toLocaleDateString('id-ID')}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => fetchRegistrationDetail(reg.registration_id)}
                              className="text-[#5A9C9B] border-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Detail
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Pendaftaran</DialogTitle>
          </DialogHeader>

          {selectedRegistration && (
            <div className="space-y-6">
              {/* Data Siswa */}
              <div>
                <h3 className="font-bold text-lg mb-3 text-[#5A9C9B]">Data Siswa</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Nama Lengkap</p>
                    <p className="font-medium">{selectedRegistration.nama_lengkap}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Nama Panggilan</p>
                    <p className="font-medium">{selectedRegistration.nama_panggilan || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Jenis Kelamin</p>
                    <p className="font-medium">{selectedRegistration.jenis_kelamin === 'L' ? 'Laki-laki' : 'Perempuan'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Tempat, Tanggal Lahir</p>
                    <p className="font-medium">{selectedRegistration.tempat_lahir}, {selectedRegistration.tanggal_lahir}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Asal Sekolah</p>
                    <p className="font-medium">{selectedRegistration.asal_sekolah}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Kelas</p>
                    <p className="font-medium">{selectedRegistration.kelas.toUpperCase()}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Alamat</p>
                    <p className="font-medium">{selectedRegistration.alamat}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Telepon</p>
                    <p className="font-medium">{selectedRegistration.telepon}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium">{selectedRegistration.email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Data Orang Tua */}
              <div>
                <h3 className="font-bold text-lg mb-3 text-[#5A9C9B]">Data Orang Tua</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Nama Ayah</p>
                    <p className="font-medium">{selectedRegistration.nama_ayah || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Telepon Ayah</p>
                    <p className="font-medium">{selectedRegistration.telepon_ayah || '-'}</p>
                  </div>
                  {selectedRegistration.alamat_ortu && (
                    <div className="col-span-2">
                      <p className="text-gray-600">Alamat Orang Tua</p>
                      <p className="font-medium">{selectedRegistration.alamat_ortu}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Program */}
              <div>
                <h3 className="font-bold text-lg mb-3 text-[#5A9C9B]">Program Bimbel</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Program</p>
                    <Badge className={`${programBadgeColor(selectedRegistration.program)} mt-1`}>
                      {selectedRegistration.program}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-600">Jadwal</p>
                    <p className="font-medium">{selectedRegistration.hari} - {selectedRegistration.waktu}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600 mb-2">Mata Pelajaran</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedRegistration.mata_pelajaran?.map((mapel, idx) => (
                        <Badge key={idx} className="bg-[#f26d0f]/10 text-[#f26d0f]">
                          {mapel}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-600">Referensi</p>
                    <p className="font-medium">{selectedRegistration.referensi}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
